<script lang="ts">
  import Icon from '$components/ui/icon/icon.svelte';
  import Button from '$components/ui/button.svelte';
  import CategoryCard from '$components/category-card.svelte';

  import { t } from '$lib/i18n';
  import { categories } from '$lib/stores/content';
  import { cn } from '$lib/utils';
  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';
  import { buildPath } from '$lib/utils/paths';

  import type {
    AssetStoryblok,
    ConfigurationStoryblok,
    PageCtaStoryblok,
    PageStoryblok,
    RegionalFeaturedPageStoryblok,
    ShopPageGroupStoryblok,
    ShopPageStoryblok
  } from '$types/bloks';
  import type { ISbStoryData } from '@storyblok/js';
  import { user } from '$lib/stores/user';
  import { drawer } from '$lib/stores/drawer';
  import { AUTH_DRAWER_IDS } from '$lib/constants';
  import LinkCard from '$components/link-card.svelte';
  import StoreSwitcher from '$components/store-switcher.svelte';
  import LangSwitcher from '$components/lang-switcher.svelte';
  import { region } from '$lib/region';

  export let configuration: ConfigurationStoryblok;

  $: isLoggedIn = !!$user;

  const mainCategory = $categories.filter(
    (c) => c.uuid === configuration.navigation_main_category
  )[0] as ISbStoryData<ShopPageGroupStoryblok>;

  const mainCategorySubCategories = $categories
    .filter((c) => mainCategory.content.sub_collections.includes(c.uuid))
    .sort(
      (a, b) =>
        mainCategory.content.sub_collections.indexOf(a.uuid) -
          mainCategory.content.sub_collections.indexOf(b.uuid) || a.uuid.localeCompare(b.uuid)
    ) as ISbStoryData<ShopPageStoryblok>[];

  // TODO: Revert this "hack" to obey collections structure
  //
  // const otherCategories = (
  //   $categories.filter((c) => configuration.navigation_other_categories?.includes(c.uuid)) || []
  // ).sort(
  //   (a, b) =>
  //     (configuration.navigation_other_categories || []).indexOf(a.uuid) -
  //       (configuration.navigation_other_categories || []).indexOf(b.uuid) ||
  //     a.uuid.localeCompare(b.uuid)
  // ) as ISbStoryData<ShopPageStoryblok>[];
  const otherCategories = configuration.navigation_other_categories as ISbStoryData<
    ShopPageStoryblok | PageStoryblok
  >[];

  let regionalizedFeaturePage: PageCtaStoryblok | undefined;

  $: {
    const featuredPages = configuration.navigation_featured_page_regionalized;
    if (featuredPages && featuredPages.length) {
      regionalizedFeaturePage = featuredPages.find((fp: RegionalFeaturedPageStoryblok) =>
        fp.regions?.includes($region)
      )?.feature_page?.[0];
    } else {
      regionalizedFeaturePage = undefined;
    }
  }

  const getImage = (
    desktopImage: AssetStoryblok | undefined,
    mobileImage: AssetStoryblok | undefined
  ) => {
    const featuredImage = mobileImage || desktopImage;

    if (!featuredImage || !featuredImage?.filename) {
      return { src: '', alt: '' };
    }

    const { src, alt } = getImageAttributes(featuredImage, {
      size: [1024, 0]
    });

    return { src, alt };
  };
</script>

<div class="relative h-full overflow-y-auto px-4 pb-4 pt-7">
  <!-- Main Category -->
  <p class="mb-4 text-xl font-semibold">
    {$t('topnav.shop-value', { value: mainCategory.name.toLowerCase() })}
  </p>
  <ul class="grid grid-cols-3 items-start gap-x-2 gap-y-4">
    {#each mainCategorySubCategories as story}
      {#if story.content.cover_image?.filename}
        {@const { src, alt } = getImageAttributes(story.content.cover_image, {
          size: [256, 0]
        })}
        <CategoryCard
          size="micro"
          href={$buildPath({
            path: story.full_slug
          })}
          label={story.content.page_title || story.name}
          image={{ src, alt }}
          mobile_image={story.content.mobile_image?.filename
            ? getImageAttributes(story.content.mobile_image, {
                size: [512, 0]
              })
            : undefined}
        />
      {:else}
        <CategoryCard
          size="micro"
          href={$buildPath({
            path: story.full_slug
          })}
          label={story.content.page_title || story.name}
        />
      {/if}
    {/each}
    <CategoryCard
      size="micro"
      href={$buildPath({
        path: mainCategory.full_slug
      })}
      label={$t('topnav.shop-all')}
    />
  </ul>

  <!-- Others -->
  <p class="mb-4 mt-10 text-xl font-semibold">{$t('topnav.shop-other')}</p>
  <ul class="grid grid-cols-3 items-start gap-x-2 gap-y-4">
    {#each otherCategories as story}
      {#if story.content.cover_image?.filename}
        {@const { src, alt } = getImageAttributes(story.content.cover_image, {
          size: [256, 0]
        })}
        <CategoryCard
          size="micro"
          href={$buildPath({
            path: story.full_slug
          })}
          label={story.content.page_title || story.name}
          image={{ src, alt }}
        />
      {:else if story.content.navigation_image?.filename}
        {@const { src, alt } = getImageAttributes(story.content.navigation_image, {
          size: [256, 0]
        })}
        <CategoryCard
          size="micro"
          href={$buildPath({
            path: story.full_slug
          })}
          label={story.content.page_title || story.name}
          image={{ src, alt }}
        />
      {:else}
        <CategoryCard
          size="micro"
          href={$buildPath({
            path: story.full_slug
          })}
          label={story.content.page_title || story.name}
        />
      {/if}
    {/each}
  </ul>

  <div class="mt-10">
    {#if regionalizedFeaturePage && (regionalizedFeaturePage?.mobile_image?.filename || regionalizedFeaturePage?.image?.filename)}
      {@const { src } = getImage(
        regionalizedFeaturePage?.image,
        regionalizedFeaturePage?.mobile_image
      )}

      {@const { href } = $getAnchorFromCmsLink(regionalizedFeaturePage.link)}
      {#if href}
        <a
          {href}
          class="mt-4 h-72 w-full isolate grid place-items-center rounded-sm bg-cover relative after:rounded-sm after:absolute after:inset-0 after:bg-primary/18 after:transition-colors hover:after:bg-primary/14 motion-reduce:after:transition-none"
          style="background-image: url({src}); background-position: center;"
        >
          <span class="text-3xl z-10 font-semibold text-center text-highlight"
            >{regionalizedFeaturePage.name}</span
          >
        </a>
      {/if}
    {/if}

    {#if isLoggedIn || configuration.navigation_support_link}
      <div class="mt-4 grid grid-cols-2 gap-2">
        {#if isLoggedIn}
          <LinkCard href={$buildPath({ path: '/account/profile' })}>
            <Icon icon="user" />
            <span>{$t('topnav.profile')}</span>
          </LinkCard>
        {/if}

        {#if configuration.navigation_support_link}
          {@const { href } = $getAnchorFromCmsLink(configuration.navigation_support_link)}
          <LinkCard {href} class={cn(!isLoggedIn && 'col-span-2')}>
            <Icon icon="support" />
            <span>{$t('topnav.support')}</span>
          </LinkCard>
        {/if}
      </div>
    {/if}

    <div class={cn(isLoggedIn ? 'h-[48px]' : 'h-[108px]')} />
  </div>
</div>

<div class="absolute bottom-0 left-0 z-10 w-full border-y border-primary/4 bg-background">
  <div class="flex justify-between gap-2 pt-4 px-2 pb-4">
    <div class="flex items-center">
      <StoreSwitcher />
    </div>
    <div class="flex gap-2 flex-col items-end">
      <LangSwitcher />
    </div>
  </div>
  {#if !isLoggedIn}
    <div class="w-full gap-2 pb-4 px-2">
      <Button
        name={AUTH_DRAWER_IDS.LOGIN}
        on:click={() => {
          drawer.open(AUTH_DRAWER_IDS.LOGIN);
        }}
        class="w-full"
      >
        {$t('topnav.signin')}
      </Button>
    </div>
  {/if}
</div>
